import React, { useState, useRef, useEffect, useCallback } from "react";
import trash from "../../assests/trash.svg";

interface SwipeItemProps {
  children: React.ReactNode;
  onDone: () => void;
  onDelete: () => void;
  isPreparing: boolean;
}

// Keep track of all open swipes
const openSwipes = new Set<HTMLElement>();

const SwipeItem: React.FC<SwipeItemProps> = ({
  children,
  onDone,
  onDelete,
  isPreparing,
}) => {
  const [translateX, setTranslateX] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const startX = useRef(0);
  const isDragging = useRef(false);
  const isTouch = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const maxSwipe = isPreparing ? -120 : -60;

  const close = useCallback(() => {
    setTranslateX(0);
    setIsOpen(false);
    if (containerRef.current) openSwipes.delete(containerRef.current);
  }, []);

  const openSwipe = useCallback(() => {
    // close all other swipes
    openSwipes.forEach((el) => {
      if (el !== containerRef.current) {
        (el as any)._swipeClose?.();
        openSwipes.delete(el);
      }
    });
    setTranslateX(maxSwipe);
    setIsOpen(true);
    if (containerRef.current) {
      openSwipes.add(containerRef.current);
      (containerRef.current as any)._swipeClose = close;
    }
  }, [maxSwipe, close]);

  const handleStart = useCallback((x: number, touch = false) => {
    isTouch.current = touch;
    isDragging.current = true;
    startX.current = x;
  }, []);

  const handleMove = useCallback(
    (x: number) => {
      if (!isDragging.current) return;
      const deltaX = x - startX.current;
      if (deltaX < 0) setTranslateX(Math.max(deltaX, maxSwipe));
    },
    [maxSwipe]
  );

  const handleEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (translateX < -30) openSwipe();
    else close();
  }, [translateX, openSwipe, close]);

  const handleClick = useCallback(() => {
    // Ignore synthetic click that fires right after a touch interaction.
    if (isTouch.current) {
      isTouch.current = false;
      return;
    }
    if (isOpen) close();
    else openSwipe();
  }, [isOpen, close, openSwipe]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpen, close]);

  return (
    <div className="relative overflow-hidden bg-white" ref={containerRef}>
      {/* Action buttons */}
      <div className="absolute right-0 top-0 bottom-0 flex gap-2 items-start pt-2 pr-2 bg-white">
        {isPreparing && (
          <button
            onClick={() => {
              onDone();
              close();
            }}
            className="bg-[#7CBF80] text-white font-outfit border-none rounded-lg py-3 px-2 text-xs font-semibold cursor-pointer"
          >
            Done
          </button>
        )}
        <button
          onClick={() => {
            onDelete();
            close();
          }}
          className="bg-red-500 text-white border-none rounded-lg py-3 px-2 text-xs font-semibold cursor-pointer"
        >
          <img src={trash} alt="Logo" className="w-[17px] h-[17px]" />
        </button>
      </div>

      {/* Swipeable content */}
      <div
        className="transition-transform bg-white select-none"
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging.current ? "none" : "transform 0.2s ease",
        }}
        onTouchStart={(e) => handleStart(e.touches[0].clientX, true)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={handleEnd}
        onMouseDown={(e) => handleStart(e.clientX, false)}
        onMouseMove={(e) => handleMove(e.clientX)}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onClick={handleClick}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeItem;

// import React, { useState, useRef, useCallback } from "react";
// import trash from "../../assests/trash.svg";

// interface SwipeItemProps {
//   children: React.ReactNode;
//   onDone: () => void;
//   onDelete: () => void;
//   isPreparing: boolean;
// }

// const SwipeItem: React.FC<SwipeItemProps> = ({
//   children,
//   onDone,
//   onDelete,
//   isPreparing,
// }) => {
//   const [translateX, setTranslateX] = useState<number>(0);
//   const [open, setOpen] = useState<boolean>(false);
//   const startX = useRef<number>(0);
//   const isTouchEvent = useRef<boolean>(false); // To track if it's a touch event or mouse event
//   const isTouchDevice =
//     typeof window !== "undefined" && "ontouchstart" in window;

//   const maxSwipeDistance = isPreparing ? -120 : -60; // Adjust swipe distance based on `isPreparing`

//   const handleTouchStart = useCallback((e: React.TouchEvent): void => {
//     isTouchEvent.current = true;
//     startX.current = e.touches[0].clientX;
//   }, []);

//   const handleTouchMove = useCallback(
//     (e: React.TouchEvent): void => {
//       const deltaX = e.touches[0].clientX - startX.current;
//       if (deltaX < 0) {
//         setTranslateX(Math.max(deltaX, maxSwipeDistance)); // Adjust swipe max distance based on `isPreparing`
//       }
//     },
//     [maxSwipeDistance]
//   );

//   const handleTouchEnd = useCallback((): void => {
//     if (translateX < -30) {
//       setTranslateX(maxSwipeDistance); // snap open to max distance
//       setOpen(true);
//     } else {
//       setTranslateX(0); // snap shut
//       setOpen(false);
//     }
//   }, [translateX, maxSwipeDistance]);

//   const handleClick = useCallback(() => {
//     if (isTouchDevice) {
//       // ✅ On mobile: only allow click to close
//       if (open) {
//         setTranslateX(0);
//         setOpen(false);
//       }
//     } else {
//       // ✅ On desktop: toggle open/close
//       if (open) {
//         setTranslateX(0);
//         setOpen(false);
//       } else {
//         setTranslateX(maxSwipeDistance); // Apply the max swipe distance
//         setOpen(true);
//       }
//     }
//   }, [open, isTouchDevice, maxSwipeDistance]);

//   const close = useCallback(() => {
//     setTranslateX(0);
//     setOpen(false);
//   }, []);

//   return (
//     <div className="relative overflow-hidden bg-white">
//       {/* Action buttons */}
//       <div className="absolute right-0 top-0 bottom-0 flex gap-2 items-start pt-2 pr-2 bg-white">
//         {/* Only show the Done button if the order is preparing */}
//         {isPreparing && (
//           <button
//             onClick={() => {
//               onDone();
//               close();
//             }}
//             className="bg-[#7CBF80] text-white font-outfit border-none rounded-lg py-3 px-2 text-xs font-semibold"
//           >
//             Done
//           </button>
//         )}
//         <button
//           onClick={() => {
//             onDelete();
//             close();
//           }}
//           className="bg-red-500 text-white border-none rounded-lg py-3 px-2 text-xs font-semibold"
//         >
//           <img src={trash} alt="Logo" className="w-[17px] h-[17px]" />
//         </button>
//       </div>

//       {/* Swipeable content */}
//       <div
//         className="transition-transform bg-white"
//         style={{
//           transform: `translateX(${translateX}px)`,
//           transition: "transform 0.2s ease",
//         }}
//         onTouchStart={handleTouchStart}
//         onTouchMove={handleTouchMove}
//         onTouchEnd={handleTouchEnd}
//         onClick={handleClick}
//       >
//         {children}
//       </div>
//     </div>
//   );
// };

// export default SwipeItem;
